import React from "react";
import { Link } from "react-router-dom";

const ProductCard = ({ product, onEnquire }) => {
    const specs = product?.specifications || {};
    const modelNumber = product?.modelNumber || product?.sku || "DXAX-SERIES";
    const ipRating = specs.protectionRating || specs.ipRating || (product?.category?.toLowerCase().includes("outdoor") ? "IP65" : "IP20");
    const power = specs.ratedPower || (product?.name?.match(/\d+W/i) ? product.name.match(/\d+W/i)[0] : "150W");
    const throwDistance = specs.projectionDistance || "3 - 25 Metres";
    const lightSource = specs.lightSourceBrand ? `${power} ${specs.lightSourceBrand}` : `${power} OSRAM`;
    const imageSrc = product?.images?.[0] || product?.image || "/assets/lumigo/projector-1.jpg";

    const standardPrice = product?.pricing?.mrp || product?.price || 0;
    const discountedPrice = product?.pricing?.finalPrice || product?.discountPrice || product?.price || 0;
    const hasDiscount = product?.pricing?.isOnOffer || (standardPrice > discountedPrice && discountedPrice > 0);
    const discountPercent = hasDiscount && standardPrice > 0 ? Math.round(((standardPrice - discountedPrice) / standardPrice) * 100) : 0;

    return (
        <div className="group bg-white rounded-2xl overflow-hidden border border-[#E5E7EB] hover:border-[#E3B629] hover:shadow-xl transition-all duration-300 flex flex-col h-full">
            {/* Top Pill / Badge */}
            <div className="p-4 pb-0 flex items-center justify-between">
                {product?.isFeatured ? (
                    <span className="bg-[#FEF3C7] text-[#92400E] text-[10px] font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wider">
                        Best Seller
                    </span>
                ) : (
                    <span className="bg-[#F7F5EF] text-[#687482] text-[10px] font-semibold px-2.5 py-0.5 rounded-full">
                        {product?.category || "Commercial Gobo"}
                    </span>
                )}
            </div>

            {/* Product Image Stage */}
            <div className="relative aspect-[4/3] w-full bg-[#FFFFFF] overflow-hidden flex items-center justify-center p-6">
                <Link to={`/product/${product._id}`} className="block w-full h-full flex items-center justify-center">
                    <img
                        src={imageSrc}
                        alt={product.name}
                        className="max-w-full max-h-full object-contain transform group-hover:scale-108 transition-transform duration-500"
                        loading="lazy"
                        onError={(e) => { e.target.onerror = null; e.target.src = "/assets/lumigo/projector-1.jpg"; }}
                    />
                </Link>
            </div>

            {/* Product Info */}
            <div className="p-5 pt-0 flex-1 flex flex-col justify-between">
                <div>
                    <Link to={`/product/${product._id}`} className="block mb-2.5 group-hover:text-[#E3B629] transition-colors">
                        <span className="block text-sm font-extrabold text-[#263746]">{modelNumber}</span>
                        <h3 className="text-xs font-semibold text-[#687482] leading-snug line-clamp-1">
                            {product.name.replace(modelNumber, '').trim() || product.category}
                        </h3>
                    </Link>

                    {/* Bullet Specs matching Image 1 */}
                    <div className="space-y-1 mb-4 text-xs text-[#687482]">
                        <div className="flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#E3B629] flex-shrink-0" />
                            <span>{throwDistance}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#E3B629] flex-shrink-0" />
                            <span>{lightSource}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#E3B629] flex-shrink-0" />
                            <span>{ipRating} Waterproof</span>
                        </div>
                    </div>
                </div>

                {/* Price and Buttons */}
                <div>
                    <div className="flex items-baseline gap-2 mb-4">
                        {discountedPrice > 0 ? (
                            <>
                                <span className="text-lg font-extrabold text-[#263746] font-mono">
                                    ₹{discountedPrice.toLocaleString()}
                                </span>
                                {hasDiscount && discountPercent > 0 && (
                                    <>
                                        <span className="text-xs text-[#9CA3AF] line-through font-mono">
                                            ₹{standardPrice.toLocaleString()}
                                        </span>
                                        <span className="bg-[#FFE4E6] text-[#E11D48] text-[10px] font-bold px-1.5 py-0.5 rounded">
                                            {discountPercent}% OFF
                                        </span>
                                    </>
                                )}
                            </>
                        ) : (
                            <span className="text-base font-bold text-[#263746]">On Request</span>
                        )}
                    </div>

                    {/* Action Buttons matching Image 1 */}
                    <div className="grid grid-cols-2 gap-2">
                        <Link
                            to={`/product/${product._id}`}
                            className="bg-[#E3B629] hover:bg-[#cfa320] text-[#263746] font-bold text-xs py-2 rounded-lg text-center transition shadow-sm"
                        >
                            View Details
                        </Link>
                        <button
                            onClick={() => (onEnquire ? onEnquire(product) : (window.location.href = `/product/${product._id}`))}
                            className="border border-[#E5E7EB] hover:bg-[#F7F5EF] text-[#263746] font-semibold text-xs py-2 rounded-lg text-center transition"
                        >
                            Enquire Now
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductCard;
